import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@material-ui/core';
import { FilterIcon } from '../../../icons';
import TablePager from '../../../components/atom/TablePager';
import { doSwaggerCall, useSwaggerClient } from '../../../hooks/useSwaggerClient';
import AdminHeader from '../../../components/atom/AdminHeader';
import CustomReportFiltersSidebar, {
    filterFields,
    dateTypeOptions
} from './components/CustomReportFiltersSidebar';
import { exportToCsv } from '../../../excelExportHelpers';
import { useNotify } from '../../../hooks/useNotify';
import { formatMoney } from '../../../utils';
import moment from 'moment';

const initialFilters = {
    dateFrom: '',
    dateTo: '',
    dateType: 'activity',
    selectedVendors: [],
    selectedClients: [],
    selectedAgents: [],
    selectedActivities: [],
    selectedUserGroups: [],
    groupBy: 'none'
};

const AppliedFilters = ({ filters, setFilters }) => {
    const filterEntries = Object.entries(filters).filter(([key, value]) => {
        if (key === 'groupBy') {
            return value !== 'none';
        }
        if (key === 'dateType') {
            // Only show dateType if there's a date filter applied
            return (filters.dateFrom || filters.dateTo) && value !== 'activity';
        }
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return value;
    });

    if (filterEntries.length === 0) {
        return null;
    }

    const getFilterLabel = (key, value) => {
        if (Array.isArray(value)) {
            return `${filterFields[key]?.label || key}: ${value.length} selected`;
        }
        if (key === 'dateType') {
            const option = dateTypeOptions.find(o => o.value === value);
            return `${filterFields[key]?.label || key}: ${option?.label || value}`;
        }
        return `${filterFields[key]?.label || key}: ${value}`;
    };

    return (
        <Box display="flex" alignItems="center" style={{ marginLeft: 20 }}>
            <Box style={{ marginRight: 10, display: 'flex', alignItems: 'center' }}>
                <FilterIcon style={{ verticalAlign: 'middle', marginRight: 5 }} />
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    Applied filters:
                </Typography>
            </Box>
            {filterEntries.map(([key, value]) => (
                <Chip
                    size="small"
                    key={key}
                    label={getFilterLabel(key, value)}
                    style={{ marginRight: 5 }}
                    onDelete={() => {
                        setFilters(prevFilters => ({
                            ...prevFilters,
                            [key]: initialFilters[key]
                        }));
                    }}
                />
            ))}
        </Box>
    );
};

const CustomReportPage = () => {
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);
    const [isExporting, setIsExporting] = useState(false);
    const { notifyError, notifySuccess } = useNotify();

    const getApiParams = useCallback(
        (overridePage, overridePageSize) => ({
            page: overridePage ?? page,
            pageSize: overridePageSize ?? pageSize,
            dateFrom: appliedFilters.dateFrom || null,
            dateTo: appliedFilters.dateTo || null,
            dateType: appliedFilters.dateType || 'activity',
            agentIds: appliedFilters.selectedAgents,
            userGroupIds: appliedFilters.selectedUserGroups,
            activities: appliedFilters.selectedActivities,
            clients: appliedFilters.selectedClients,
            vendors: appliedFilters.selectedVendors,
            groupReportBy: appliedFilters.groupBy === 'none' ? null : appliedFilters.groupBy
        }),
        [page, pageSize, appliedFilters]
    );

    // eslint-disable-next-line no-unused-vars
    const [data, loading, error, setData, reload] = useSwaggerClient(
        'reports',
        'getCustomReports',
        getApiParams()
    );

    useEffect(() => {
        if (loading) {
            return;
        }

        if (data?.reports?.length === 0 && page !== 0) {
            setPage(0);
        }
    }, [page, data, loading]);

    const handleSearch = () => {
        setPage(0);
        setAppliedFilters({ ...filters });
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const allReports = [];
            let currentPage = 0;
            const exportPageSize = 100;
            let hasMore = true;

            while (hasMore) {
                const params = {
                    page: currentPage,
                    pageSize: exportPageSize,
                    dateFrom: appliedFilters.dateFrom || null,
                    dateTo: appliedFilters.dateTo || null,
                    dateType: appliedFilters.dateType || 'activity',
                    agentIds: appliedFilters.selectedAgents,
                    userGroupIds: appliedFilters.selectedUserGroups,
                    activities: appliedFilters.selectedActivities,
                    clients: appliedFilters.selectedClients,
                    vendors: appliedFilters.selectedVendors,
                    groupReportBy: appliedFilters.groupBy === 'none' ? null : appliedFilters.groupBy
                };

                const result = await doSwaggerCall('reports', 'getCustomReports', params);
                allReports.push(...result.reports);

                const totalFetched = (currentPage + 1) * exportPageSize;
                hasMore = totalFetched < result.total;
                currentPage++;
            }

            const payoutNamesSet = new Set();
            for (const report of allReports) {
                const payouts = report.booking_payouts || [];
                for (const p of payouts) {
                    if (p?.name) {
                        payoutNamesSet.add(p.name);
                    }
                }
            }
            const payoutHeaders = Array.from(payoutNamesSet);

            // Build CSV rows - each payout becomes its own line
            const headers = [
                'Vendor',
                'Client',
                'Agent',
                'Activity',
                'User Group',
                ...payoutHeaders.flatMap(name => [`${name} - Net`, `${name} - Tax`])
            ];
            const rows = [headers];

            for (const report of allReports) {
                const baseRow = [
                    report.vendor || 'N/A',
                    report.client || 'N/A',
                    report.agent || 'N/A',
                    report.activity || 'N/A',
                    report.userGroup || 'N/A'
                ];

                const payoutByName = new Map();

                for (const p of report.booking_payouts || []) {
                    if (!p?.name) {
                        continue;
                    }

                    // Accumulate payouts with the same name
                    const prev = payoutByName.get(p.name) || { net: 0, tax: 0 };
                    payoutByName.set(p.name, {
                        net: prev.net + (Number(p.total_net) || 0),
                        tax: prev.tax + (Number(p.total_tax) || 0)
                    });
                }

                // Build dynamic columns based on payout names
                const dynamicCols = payoutHeaders.flatMap(name => {
                    const found = payoutByName.get(name);
                    return [
                        found ? `$ ${found.net.toFixed(2)}` : `$ 0.00`,
                        found ? `$ ${found.tax.toFixed(2)}` : `$ 0.00`
                    ];
                });

                rows.push([...baseRow, ...dynamicCols]);
            }

            const timestamp = moment().format('ddd MMM DD, YYYY');
            exportToCsv(`custom_report_${timestamp}.csv`, rows);
            notifySuccess(`Exported ${allReports.length} reports successfully`);
        } catch (err) {
            console.error('Export error:', err);
            notifyError('Failed to export data');
            throw err;
        } finally {
            setIsExporting(false);
        }
    };

    if (error) {
        return (
            <Container className="activity-edit page" maxWidth="none">
                <Grid container justifyContent="center">
                    <Typography variant="h4" color="error">
                        Error loading reports
                    </Typography>
                </Grid>
            </Container>
        );
    }

    return (
        <Container className="activity-edit page" maxWidth="none">
            <Grid className="pageWithSidebar">
                <CustomReportFiltersSidebar
                    filters={filters}
                    setFilters={setFilters}
                    onSearch={handleSearch}
                    onExport={handleExport}
                    isExporting={isExporting}
                    total={data?.total || 0}
                />
                <Grid className="pageContent">
                    <Grid container>
                        <AdminHeader title="Payouts" childrenBefore={true}>
                            <AppliedFilters
                                filters={appliedFilters}
                                setFilters={newFilters => {
                                    setAppliedFilters(newFilters);
                                    setFilters(newFilters);
                                }}
                            />
                        </AdminHeader>
                        <Grid item container xs={12} justifyContent="center">
                            {loading ? (
                                <CircularProgress style={{ margin: 50 }} />
                            ) : (
                                <>
                                    {data?.reports?.length === 0 ? (
                                        <div style={{ textAlign: 'center', marginTop: 50 }}>
                                            <h3>No reports found.</h3>
                                            <p>Adjust your filters and try again.</p>
                                        </div>
                                    ) : (
                                        <TableContainer>
                                            <Table
                                                style={{ backgroundColor: 'white' }}
                                                size="small"
                                            >
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Vendor</TableCell>
                                                        <TableCell>Client</TableCell>
                                                        <TableCell>Agent</TableCell>
                                                        <TableCell>Activity</TableCell>
                                                        <TableCell>User Group</TableCell>
                                                        <TableCell>Payout Name</TableCell>
                                                        <TableCell align="right">Net</TableCell>
                                                        <TableCell align="right">Tax</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {data?.reports?.map((report, index) => {
                                                        const payouts =
                                                            report.booking_payouts?.length > 0
                                                                ? report.booking_payouts
                                                                : [null];
                                                        return payouts.map((payout, idx) => (
                                                            <TableRow
                                                                key={`${report.id || index}-${idx}`}
                                                            >
                                                                {idx === 0 && (
                                                                    <>
                                                                        <TableCell
                                                                            rowSpan={payouts.length}
                                                                        >
                                                                            {report.vendor || 'N/A'}
                                                                        </TableCell>
                                                                        <TableCell
                                                                            rowSpan={payouts.length}
                                                                        >
                                                                            {report.client || 'N/A'}
                                                                        </TableCell>
                                                                        <TableCell
                                                                            rowSpan={payouts.length}
                                                                        >
                                                                            {report.agent || 'N/A'}
                                                                        </TableCell>
                                                                        <TableCell
                                                                            rowSpan={payouts.length}
                                                                        >
                                                                            {report.activity ||
                                                                                'N/A'}
                                                                        </TableCell>
                                                                        <TableCell
                                                                            rowSpan={payouts.length}
                                                                        >
                                                                            {Array.isArray(
                                                                                report.userGroup
                                                                            ) &&
                                                                            report.userGroup
                                                                                .length === 0
                                                                                ? 'N/A'
                                                                                : report.userGroup}
                                                                        </TableCell>
                                                                    </>
                                                                )}
                                                                <TableCell>
                                                                    {payout?.name || (
                                                                        <Typography
                                                                            variant="body2"
                                                                            style={{
                                                                                color: '#999'
                                                                            }}
                                                                        >
                                                                            No payouts
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {payout
                                                                        ? formatMoney(
                                                                              payout.total_net || 0
                                                                          )
                                                                        : '-'}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {payout
                                                                        ? formatMoney(
                                                                              payout.total_tax || 0
                                                                          )
                                                                        : '-'}
                                                                </TableCell>
                                                            </TableRow>
                                                        ));
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </>
                            )}
                            <Grid container justifyContent="flex-end">
                                <TablePager
                                    total={data?.total || 0}
                                    page={page}
                                    setPage={setPage}
                                    pageSize={pageSize}
                                    setPageSize={setPageSize}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    );
};

export default CustomReportPage;
